import React from "react";
import {
  FormField,
  Popover,
  Select,
  StatusIndicator,
} from "@amzn/awsui-components-console";
import { useValidationUtils } from "./ValidationUtils";

interface ValidatedSelectFieldProps {
  label: string;
  fieldName: string;
  value: string | null;
  options: Array<{ label: string; value: string }>;
  placeholder: string;
  onChange: (value: string) => void;
  useCaseName?: string | null;
  uslLineIndex?: number | null;
  validationUtils: ReturnType<typeof useValidationUtils>;
  onFocus?: (
    fieldName: string,
    useCaseName?: string | null,
    uslLineIndex?: number | null,
  ) => void;
  onBlur?: () => void;
  /** When true, render a "modified field" indicator next to the label. */
  isChanged?: boolean;
}

export const ValidatedSelectField: React.FC<ValidatedSelectFieldProps> = ({
  label,
  fieldName,
  value,
  options,
  placeholder,
  onChange,
  useCaseName = null,
  uslLineIndex = null,
  validationUtils,
  onFocus,
  onBlur,
  isChanged = false,
}) => {
  const {
    getFieldTooltipContent,
    getFieldValidationProps,
    getFieldErrors,
    getFieldTextColor,
  } = validationUtils;
  const tooltipContent = getFieldTooltipContent(
    fieldName,
    useCaseName,
    uslLineIndex,
  );
  const validationProps = getFieldValidationProps(
    fieldName,
    useCaseName,
    uslLineIndex,
  );

  // Use the same logic as text color to determine if field should have yellow border
  const textColor = getFieldTextColor(fieldName, useCaseName, uslLineIndex);
  const hasWarning = textColor === "#855900"; // Yellow color indicates deriving attribute

  const handleFocus = () => {
    // Only trigger focus for derived attributes (red fields)
    const errors = getFieldErrors(fieldName, useCaseName, uslLineIndex);
    const hasErrorField = errors.some((error) =>
      error.derivedAttributes?.includes(fieldName),
    );

    if (hasErrorField && onFocus) {
      onFocus(fieldName, useCaseName, uslLineIndex);
    }
  };

  const handleBlur = () => {
    if (onBlur) {
      onBlur();
    }
  };

  const labelNode = (
    <span>
      {label}
      {isChanged && (
        <>
          {" "}
          <span
            className="bart-modified-indicator"
            title="Modified from original"
            aria-label="Modified field"
          >
            ●
          </span>
        </>
      )}
      {tooltipContent && (
        <>
          {" "}
          <Popover
            renderWithPortal
            size="large"
            position="top"
            dismissButton={false}
            content={tooltipContent}
          >
            <span style={{ cursor: "pointer", color: "#d13212" }}>
              <StatusIndicator type="error">Error</StatusIndicator>
            </span>
          </Popover>
        </>
      )}
    </span>
  );

  return (
    <FormField label={labelNode} stretch>
      <div
        style={{
          border: hasWarning ? "2px solid #855900" : "2px solid transparent",
          borderRadius: "4px",
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        <Select
          selectedOption={value ? { label: value, value } : null}
          onChange={({ detail }) =>
            onChange(detail.selectedOption?.value || "")
          }
          options={options}
          placeholder={placeholder}
          expandToViewport
          {...validationProps}
        />
      </div>
    </FormField>
  );
};
